import { Inject, Injectable, Optional } from '@angular/core';
import { ENV } from '@rd/core/tokens/rd-lib.token';
import { Promocode, PromocodeUsage } from '@rd/types/promocode.type';
import { EnvironmentType } from 'global-types';
import { CartDataModel } from '../../models/cart-data.model';
import { CartModel } from '../../models/cart.model';
import { CartItem, CartItemType } from '../../types/cart-item.type';
import { Cart } from '../../types/cart.type';
import { DeliveryMethod } from '../../types/delivery-method.type';

@Injectable({
  providedIn: 'root',
})
export class CartItemTransformService {
  constructor(@Optional() @Inject(ENV) private env: EnvironmentType) {
    this.env ??= 'local';
  }

  transformCartItemModel(cartModel: CartModel): CartItem {
    const { id, attributes } = cartModel;
    const {
      displayDetails: { description, productImage, available },
      orderDetails: { displayValue, productName, pointsPercentage },
      productType,
      quantity,
      supplierId,
      pointsAmount,
      cashAmount,
      pointsDiscount,
      pointsPaid,
      cashDiscount,
      cashPaid,
    } = attributes;
    return {
      id,
      available:
        this.env === 'production'
          ? available
          : this.fakeAvailable(productName, available),
      cardId: 'card id',
      deliveryMethods: this.deliveryMethodFromCartItemType(productType),
      description,
      displayValue,
      maxQuantity: 50,
      pointsPercentage,
      productImage,
      productName,
      productType,
      quantity,
      supplierId,
      pointsAmount,
      cashAmount,
      pointsDiscount,
      pointsPaid,
      cashDiscount,
      cashPaid,
    };
  }

  transformCartModel(
    cartItemModel: CartModel[],
    cartDataModel: CartDataModel,
  ): Cart {
    const cart: Cart = {
      cartItems: [],
      promocode: null,
      promocodeUsage: null,
    };

    cartItemModel.forEach((cartModel: CartModel) => {
      if (cartModel.type === 'promo_code') {
        cart.promocode = this.transformPromocode(cartModel);
      }
      if (cartModel.type === 'promo_code_usage') {
        cart.promocodeUsage = this.transformPromocodeUsage(
          cartModel,
          cartDataModel,
        );
      }
      if (
        cartModel.type === 'gift_card_cart_item' ||
        cartModel.type === 'physical_gift_card_cart_item' ||
        cartModel.type === 'merchandise_cart_item'
      ) {
        cart.cartItems.push(this.transformCartItemModel(cartModel));
      }
    });

    return cart;
  }

  transformPromocode(cartModel: CartModel): Promocode {
    const { code } = cartModel.attributes;
    return {
      code,
    };
  }

  transformPromocodeUsage(
    cartModel: CartModel,
    cartDataModel: CartDataModel,
  ): PromocodeUsage {
    const { status, remark, cashDiscount, pointsDiscount } =
      cartModel.attributes;
    const { pointsAmount, pointsPaid, cashAmount, cashPaid } =
      cartDataModel.attributes;
    return {
      status,
      remark,
      cashDiscount,
      pointsDiscount,
      pointsAmount,
      pointsPaid,
      cashAmount,
      cashPaid,
    };
  }

  // TODO: REMOVE when we can intentionally set cart items as unvailable
  // Set AP test HH to always be false in staging
  private fakeAvailable(productName: string, available: boolean): boolean {
    if (['AP test HH'].includes(productName)) {
      return false;
    } else {
      return available;
    }
  }

  private deliveryMethodFromCartItemType(
    cartItemType: CartItemType,
  ): DeliveryMethod[] {
    if (cartItemType === 'physical_gift_card') {
      return ['address'];
    }
    if (cartItemType === 'merchandise') {
      return ['address'];
    }
    if (cartItemType === 'gift_card') {
      return ['email'];
    }

    return [];
  }
}
